import ActionIconButton from '@components/Common/ActionIconButton';
import FormField from '@components/Common/FormField';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ImageSearchOutlinedIcon from '@mui/icons-material/ImageSearchOutlined';
import { Alert, Box } from '@mui/material';
import React from 'react';

import GuideImagePicker from './GuideImagePicker';
import { readHeroImage, withHeroImage } from './guideHeroImage';

interface HeroImageFieldProps {
  /** The guide body as JSON text. The hero lives at `seo.ogImage` inside it. */
  contentJson: string;
  onChange: (nextJson: string) => void;
  slug: string;
}

/**
 * The guide's hero image.
 *
 * It edits one key inside `contentJson`, which is otherwise a raw JSON textarea. That is worth a
 * dedicated control because of what the key does: `seo.ogImage` is the social preview, the card
 * image on the guide hub, and the banner on the guide itself, all three from one value. Asking an
 * author to find and type an S3 URL into a JSON blob to get all of that is why none of the sixteen
 * live guides has an image.
 *
 * When the body does not currently parse, the control says so and refuses rather than writing.
 * `withHeroImage` returns the text untouched in that case, so the failure is visible here instead
 * of appearing as a button that does nothing.
 */
const HeroImageField: React.FC<HeroImageFieldProps> = ({ contentJson, onChange, slug }) => {
  const hero = readHeroImage(contentJson);

  const parses = React.useMemo(() => {
    try {
      const value = JSON.parse(contentJson || '{}');
      return !!value && typeof value === 'object' && !Array.isArray(value);
    } catch {
      return false;
    }
  }, [contentJson]);

  if (!parses) {
    return (
      <Alert severity="info">
        The hero image is stored inside the guide body, so it can be set once the body is valid
        JSON.
      </Alert>
    );
  }

  return (
    <Box className="ge-hero">
      {hero ? (
        <>
          {/* Thumbnail and its two actions on one row, the actions as icons beside the picture
              they act on. `Remove` was a letter-spaced red text button floating next to a
              caption; "Replace hero image" was a full button under it. */}
          <Box className="ge-hero__row">
            <img className="ge-hero__thumb" src={hero.url} alt={hero.alt} />
            <Box className="ge-hero__actions">
              <GuideImagePicker
                slug={slug}
                label="Replace hero image"
                iconOnly
                icon={<ImageSearchOutlinedIcon />}
                onPicked={(image) =>
                  onChange(withHeroImage(contentJson, { url: image.url, alt: image.alt }))
                }
              />
              <ActionIconButton
                label="Remove hero image"
                icon={<DeleteOutlineIcon />}
                onClick={() => onChange(withHeroImage(contentJson, null))}
              />
            </Box>
          </Box>
          {/* Alt text lives beside the URL at seo.ogImageAlt, so it is editable here rather than
              only at upload time. "No alt text set." used to sit here with nothing to set it. */}
          <FormField
            required={false}
            label="Alt text"
            name="heroAlt"
            value={hero.alt}
            onChange={(_, v) =>
              onChange(withHeroImage(contentJson, { url: hero.url, alt: String(v ?? '') }))
            }
            helperText={
              hero.alt.trim()
                ? 'What the image shows, for a reader who cannot see it.'
                : 'Missing. Describe what the image shows, for a reader who cannot see it.'
            }
            textFieldProps={{
              slotProps: { formHelperText: { className: hero.alt.trim() ? undefined : 'ge-helper--over' } },
            }}
          />
        </>
      ) : (
        <>
          <p className="cc-ed__meta">
            No hero image. The hub card falls back to a topic tile and the guide has no banner.
          </p>
          <Box className="cc-ed__group-actions">
            <GuideImagePicker
              slug={slug}
              label="Set hero image"
              onPicked={(image) =>
                onChange(withHeroImage(contentJson, { url: image.url, alt: image.alt }))
              }
            />
          </Box>
        </>
      )}

      <p className="cc-ed__hint">
        Used for the social preview, the card on the guide hub and the banner on the guide. Landscape
        works best; the card crops to 16:9.
      </p>
    </Box>
  );
};

export default HeroImageField;
